/**
 *
 * Example usage:
 * ```
 * <Button></Button>
 * ```
 *
 * @packageDocumentation
 * @module components/Button
 */
import { createElement, CSSProperties, forwardRef } from 'react';
import { BaseButton } from './__internal__';
import { getLogger } from '@sas/nova-core/logging';
import { isNullish } from '@sas/nova-core/typeguards';
import { ButtonProps, ButtonState, ButtonTokens, ButtonVariant } from '.';
import { renderInlineCssVars, useDesignTokens } from '../../theme';

const logger = getLogger('Button');

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  if (isNullish(props.icon) && (isNullish(props.text) || props.text.trim() === '')) {
    logger.error('An icon or text must be set, and text cannot be blank.');
  }

  return <BaseButton {...props} ref={ref} />;
});

export function getButtonDesignTokens({
  disabled,
  dt,
  variant,
  tokens,
}: {
  dt: ReturnType<typeof useDesignTokens>;
  variant: ButtonVariant;
  disabled?: boolean;
  tokens?: ButtonTokens | null;
}): CSSProperties {
  let stateTokens;

  const darkVariants = new Set([
    ButtonVariant.Primary,
    ButtonVariant.PrimarySmall,
    ButtonVariant.PrimaryLight,
    ButtonVariant.PrimaryIcon,
  ]);

  if (variant === ButtonVariant.Primary || variant === ButtonVariant.PrimarySmall) {
    stateTokens = dt.enableNovaTokens ? dt.statePrimaryNova : dt.statePrimary;
  } else if (variant === ButtonVariant.PrimaryLight || variant === ButtonVariant.PrimaryIcon) {
    stateTokens = dt.enableNovaTokens ? dt.statePrimaryContentNova : dt.statePrimaryContent;
  } else if (
    variant === ButtonVariant.Light ||
    variant === ButtonVariant.Icon ||
    variant === ButtonVariant.LightSmall ||
    variant === ButtonVariant.SmallIcon
  ) {
    stateTokens = dt.enableNovaTokens ? dt.stateStandardContentNova : dt.stateStandardContent;
  } else {
    stateTokens = dt.enableNovaTokens ? dt.stateStandardNova : dt.stateStandard;
  }

  let globalFocus;
  if (darkVariants.has(variant) && dt.themeVariant === 'light') {
    globalFocus = 'var(--focus-dark-bg)';
  } else {
    globalFocus = 'var(--focus-light-bg)';
  }

  const outlineOffset = 'calc(-2px + calc(-1 * var(--focus-width)))';
  return {
    ...renderInlineCssVars(
      {
        ...(!disabled ? stateTokens.normal : stateTokens.disabled),
        ...(tokens ? tokens({ disabled, state: ButtonState.Normal }) : undefined),
        ...{
          'animation-duration': dt.animation.duration.standard,
          'animation-timing-function': dt.animation.timingFunction.standard,
          'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
          'font-size-s': dt.enableNovaTokens ? dt.fontNova.size.s : dt.font.size.s,
        },
      },
      'button'
    ),
    ...renderInlineCssVars(
      {
        ...(!disabled ? stateTokens.hover : stateTokens.disabled),
        ...(tokens ? tokens({ disabled, state: ButtonState.Hover }) : undefined),
      },
      'button-hover'
    ),
    ...renderInlineCssVars(
      {
        ...(!disabled ? stateTokens.active : stateTokens.disabled),
        ...(tokens ? tokens({ disabled, state: ButtonState.Active }) : undefined),
      },
      'button-active'
    ),
    // ...renderInlineCssVars({ outline, 'outline-offset': outlineOffset }, 'button-focus'),
    ...renderInlineCssVars(
      { outline: globalFocus, 'outline-offset': outlineOffset },
      'button-focus'
    ),
    ...renderInlineCssVars(
      {
        'squish-s': dt.spacing.insetSquish.s.padding,
        'squish-xs': dt.spacing.insetSquish.xs.padding,
        s: dt.spacing.core.s,
        xs: dt.spacing.core.xs,
        xxs: dt.spacing.core.xxs,
      },
      'button-spacing'
    ),
  };
}
